import { useEffect, useMemo, useRef, useState } from 'react';
import { BookMarked, BookOpen, Search, ChevronRight, ChevronLeft, Wrench, X, Video, ShieldCheck, ChefHat } from 'lucide-react';
import SheetModal, { SheetKarta, SheetNadpisSkupiny, SheetTlacitko } from '../SheetModal';
import { SheetAlert } from '../../pages/MojeSmena/SheetUI';
import { SHEET_TOKENS, getInnerCardSurface, getTextColors } from '../../pages/MojeSmena/sheetStyle';
import ClanekDetail from './ClanekDetail';
import { REZERVA_LISTY } from '../MobilniLista';
import { TYPY, api, delka, barva as barvaZnalosti } from './znalosti';

const sklonujClanky = (n) => (n === 1 ? 'článek' : n >= 2 && n <= 4 ? 'články' : 'článků');

/**
 * Znalosti ze spodní lišty — jednoduchý seznam s hledáním, ne celý modul.
 *
 * Standardní sheet aplikace (`SheetModal`, jako Volné směny v Přehledu):
 * hlavička s ikonou a podnadpisem, hledání pevně v úvodu nad scrollem,
 * skupiny s nadpisem malými kapitálkami a řádky jako `SheetKarta`
 * s mezerou mezi sebou — ne nalepený seznam.
 *
 * Lišta je vidět pořád, takže ví, kde člověk je (`klic` = stránka.záložka),
 * a nahoře nabídne články k té obrazovce („Tady se hodí"). Pod nimi jsou
 * všechny publikované po kategoriích. Článek se otevře tady v sheetu —
 * obsluha modul Znalosti nemá, ten je pro administrátory pod Moduly.
 *
 * Otevírá se i odkudkoli přes `otevriZnalost({ clanek })` (krok receptu)
 * a QR štítkem (`{ qr }`).
 *
 * `jakoStranka` — ze spodní lišty se od 2026-10-04 otevírá jako stránka
 * (`znalosti-seznam`), ne okno: Znalosti jsou slot lišty jako Domů nebo
 * Zprávy, a ty jsou taky stránky. Obsah je stejný, mění se jen obal.
 */
// Řízená dokumentace ve Znalostech (stránka) — barvy jako v Dokumentaci
const DRUHY_DOK = {
    smernice: { nazev: 'Směrnice', Icon: ShieldCheck, barva: '#0CC0DF', zkratka: 'SMĚRNICE', jednotky: ['dokument', 'dokumenty', 'dokumentů'] },
    recepty:  { nazev: 'Recepty', Icon: ChefHat, barva: '#FF8A00', zkratka: 'RECEPT', jednotky: ['recept', 'recepty', 'receptů'] },
};
const bezDiakritiky = (s) => String(s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

export default function ZnalostiSheet({ klic, params = {}, isDark, isMobile, onClose, onUpravit, jakoStranka = false, dokumentace = null }) {
    const akcent = barvaZnalosti(isDark);
    const barvy = getTextColors(isDark);
    const vnitrni = getInnerCardSurface(isDark);

    const [data, setData] = useState(null);           // { clanky, kategorie }
    const [kObrazovce, setKObrazovce] = useState({ clanky: [], nazev: null });
    const [qr, setQr] = useState(null);                // { zarizeni, clanky } ze štítku
    const [hledani, setHledani] = useState('');
    const [detail, setDetail] = useState(null);
    const [nacitamDetail, setNacitamDetail] = useState(false);
    const [chyba, setChyba] = useState(null);
    // Hledání v řízené dokumentaci (jen stránka se `dokumentace`)
    const [vysledkyDok, setVysledkyDok] = useState(null);   // { smernice, recepty }
    const navodyRef = useRef(null);

    const otevrit = async (id) => {
        setNacitamDetail(true); setChyba(null);
        try { setDetail(await api(`/api/znalosti/${id}`)); }
        catch { setChyba('Článek se nepodařilo otevřít.'); }
        finally { setNacitamDetail(false); }
    };

    useEffect(() => {
        api('/api/znalosti').then(d => setData({ ...d, clanky: (d.clanky || []).filter(c => c.stav === 'publikovano') }))
            .catch(() => setData({ clanky: [], kategorie: [] }));
    }, []);
    useEffect(() => {
        if (!klic) return;
        api(`/api/znalosti/k-obrazovce?klic=${encodeURIComponent(klic)}`).then(setKObrazovce).catch(() => {});
    }, [klic]);
    useEffect(() => {
        if (params.clanek) otevrit(Number(params.clanek));
        if (params.qr) {
            api(`/api/znalosti/qr/${encodeURIComponent(params.qr)}`).then(d => {
                setQr(d);
                // U stroje se nečte seznam — jediná jednobodovka rovnou
                if (d.clanky?.length === 1) otevrit(d.clanky[0].id);
            }).catch(() => setChyba('Tenhle štítek nepatří žádnému zařízení.'));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params.clanek, params.qr]);

    const q = hledani.trim().toLowerCase();
    const maRecepty = !!dokumentace?.prehled?.druhy?.some(d => d.kod === 'recepty');
    useEffect(() => {
        if (!dokumentace || !q) { setVysledkyDok(null); return undefined; }
        let platne = true;
        const t = setTimeout(async () => {
            const [smernice, recepty] = await Promise.all([
                fetch('/api/me/dokumentace/smernice').then(r => (r.ok ? r.json() : [])).catch(() => []),
                maRecepty ? fetch(`/api/me/dokumentace/recepty?q=${encodeURIComponent(q)}`).then(r => (r.ok ? r.json() : [])).catch(() => []) : [],
            ]);
            if (!platne) return;
            const h = bezDiakritiky(q);
            setVysledkyDok({
                smernice: (Array.isArray(smernice) ? smernice : []).filter(x => bezDiakritiky(`${x.nazev} ${x.cislo_dokumentu || ''} ${x.kategorie?.nazev || ''}`).includes(h)),
                recepty: Array.isArray(recepty) ? recepty.slice(0, 30) : [],
            });
        }, 250);
        return () => { platne = false; clearTimeout(t); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [q, !!dokumentace, maRecepty]);
    const clanky = data?.clanky || [];
    const nalezene = q ? clanky.filter(c => `${c.nazev} ${c.perex || ''} ${c.kod || ''} ${(c.vazby || []).map(v => v.popis).join(' ')}`.toLowerCase().includes(q)) : null;
    // Co je nahoře v „Tady se hodí" (nebo u QR štítku), se v kategoriích neopakuje
    const nahore = useMemo(() => new Set([...(kObrazovce.clanky || []), ...(qr?.clanky || [])].map(c => c.id)), [kObrazovce, qr]);
    const podleKategorii = useMemo(() => {
        const kategorie = data?.kategorie || [];
        const zbytek = clanky.filter(c => !nahore.has(c.id));
        const skupiny = kategorie.map(k => ({ ...k, clanky: zbytek.filter(c => c.kategorie_id === k.id) }));
        const bez = zbytek.filter(c => !kategorie.some(k => k.id === c.kategorie_id));
        if (bez.length) skupiny.push({ id: 'bez', nazev: 'Ostatní', clanky: bez });
        return skupiny.filter(s => s.clanky.length);
    }, [data, clanky, nahore]);

    // ── Řádek článku — karta jako řádky v „Tým na směně" ──
    const radek = (c, zvyrazneny = false) => {
        const opl = c.typ === 'jednobodovka';
        const meta = [
            c.typ === 'jednobodovka' && c.pocet_kroku ? `${c.pocet_kroku} kroků` : null,
            c.video ? `video ${delka(c.video.delka_s) || ''}`.trim() : null,
        ].filter(Boolean).join(' · ');
        return (
            <SheetKarta key={c.id} isDark={isDark} akcent={zvyrazneny ? akcent : null} role="button" tabIndex={0}
                onClick={() => otevrit(c.id)} onKeyDown={(e) => { if (e.key === 'Enter') otevrit(c.id); }}
                style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', minHeight: 58, cursor: 'pointer' }}>
                <span style={{
                    width: SHEET_TOKENS.sizes.iconBox, height: SHEET_TOKENS.sizes.iconBox, borderRadius: 9, flexShrink: 0, overflow: 'hidden',
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    background: opl ? `${akcent}2E` : vnitrni.background, color: opl ? akcent : barvy.textMuted,
                }}>
                    {c.nahled ? <img src={c.nahled} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <BookOpen size={16} />}
                </span>
                <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: 'block', fontSize: SHEET_TOKENS.typography.bodyBold.size, fontWeight: 700, color: barvy.textPrimary, lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {c.nazev}
                    </span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 3, fontSize: SHEET_TOKENS.typography.hint.size, color: barvy.textMuted, minWidth: 0 }}>
                        <span style={{ flexShrink: 0, fontSize: 9.5, fontWeight: 800, letterSpacing: 0.5, padding: '1px 6px', borderRadius: 5, background: opl ? `${akcent}24` : vnitrni.background, color: opl ? akcent : barvy.textMuted }}>
                            {TYPY[c.typ].zkratka}
                        </span>
                        {c.video && <Video size={12} style={{ flexShrink: 0 }} />}
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{meta || c.perex || TYPY[c.typ].label}</span>
                    </span>
                </span>
                <ChevronRight size={16} style={{ color: barvy.textMuted, flexShrink: 0 }} />
            </SheetKarta>
        );
    };

    const skupina = (klicSkupiny, nadpis, polozky, zvyraznene = false) => (
        <div key={klicSkupiny} style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 10 }}>
            <SheetNadpisSkupiny isDark={isDark} style={{ margin: '2px 2px 0', color: zvyraznene ? akcent : undefined }}>{nadpis}</SheetNadpisSkupiny>
            {polozky.map(c => radek(c, zvyraznene))}
        </div>
    );

    // Řádek řízeného dokumentu (směrnice / recept) — stejná karta jako článek
    const radekDok = (druh, x, popisek, zvyrazneny = false) => {
        const cfg = DRUHY_DOK[druh];
        const Ikona = cfg.Icon;
        return (
            <SheetKarta key={`${druh}${x.id}`} isDark={isDark} akcent={zvyrazneny ? cfg.barva : null} role="button" tabIndex={0}
                onClick={() => dokumentace.jdi(druh === 'smernice' ? { druh: 'smernice', smernice: x.id } : { druh: 'recepty', recept: x.id })}
                onKeyDown={(e) => { if (e.key === 'Enter') dokumentace.jdi(druh === 'smernice' ? { druh: 'smernice', smernice: x.id } : { druh: 'recepty', recept: x.id }); }}
                style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', minHeight: 58, cursor: 'pointer' }}>
                <span style={{ width: SHEET_TOKENS.sizes.iconBox, height: SHEET_TOKENS.sizes.iconBox, borderRadius: 9, flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: `${cfg.barva}2E`, color: cfg.barva }}>
                    <Ikona size={16} />
                </span>
                <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: 'block', fontSize: SHEET_TOKENS.typography.bodyBold.size, fontWeight: 700, color: barvy.textPrimary, lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {x.nazev}
                    </span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 3, fontSize: SHEET_TOKENS.typography.hint.size, color: barvy.textMuted, minWidth: 0 }}>
                        <span style={{ flexShrink: 0, fontSize: 9.5, fontWeight: 800, letterSpacing: 0.5, padding: '1px 6px', borderRadius: 5, background: `${cfg.barva}24`, color: cfg.barva }}>{cfg.zkratka}</span>
                        {popisek && <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: zvyrazneny ? cfg.barva : undefined }}>{popisek}</span>}
                    </span>
                </span>
                <ChevronRight size={16} style={{ color: barvy.textMuted, flexShrink: 0 }} />
            </SheetKarta>
        );
    };

    // Dlaždice Recepty · Směrnice · Návody (stránka)
    const dlazdice = () => {
        const druhy = dokumentace?.prehled?.druhy || [];
        const polozky = [
            ...['recepty', 'smernice'].map(kod => {
                const d = druhy.find(x => x.kod === kod);
                if (!d) return null;
                const cfg = DRUHY_DOK[kod];
                return { klic: kod, cfg, pocet: d.pocet, kPotvrzeni: d.k_potvrzeni, onClick: () => dokumentace.jdi({ druh: kod }) };
            }),
            { klic: 'navody', cfg: { nazev: 'Návody', Icon: BookOpen, barva: akcent, jednotky: ['návod', 'návody', 'návodů'] }, pocet: clanky.length,
                onClick: () => navodyRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }) },
        ].filter(Boolean);
        return (
            <div style={{ display: 'grid', gridTemplateColumns: `repeat(${polozky.length}, minmax(0, 1fr))`, gap: 8, marginBottom: 14 }}>
                {polozky.map(p => {
                    const Ikona = p.cfg.Icon;
                    return (
                        <button key={p.klic} type="button" onClick={p.onClick}
                            style={{ ...vnitrni, textAlign: 'left', border: 'none', borderRadius: 16, padding: 12, cursor: 'pointer', fontFamily: 'inherit', display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
                            <span style={{ width: 36, height: 36, borderRadius: 10, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: `${p.cfg.barva}2E`, color: p.cfg.barva }}>
                                <Ikona size={18} />
                            </span>
                            <span style={{ fontSize: 15, fontWeight: 700, color: barvy.textPrimary }}>{p.cfg.nazev}</span>
                            <span style={{ fontSize: 12, color: barvy.textMuted, marginTop: -4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.pocet} {p.cfg.jednotky[p.pocet === 1 ? 0 : p.pocet >= 2 && p.pocet <= 4 ? 1 : 2]}</span>
                            {p.kPotvrzeni > 0 && (
                                <span style={{ alignSelf: 'flex-start', fontSize: 10.5, fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: `${p.cfg.barva}24`, color: p.cfg.barva }}>{p.kPotvrzeni} k potvrzení</span>
                            )}
                        </button>
                    );
                })}
            </div>
        );
    };

    const prazdno = (titulek, text) => (
        <div style={{ padding: '28px 8px', textAlign: 'center', color: barvy.textMuted }}>
            <BookOpen size={26} style={{ opacity: 0.4, marginBottom: 8 }} />
            <div style={{ fontSize: 14, fontWeight: 700, color: barvy.textPrimary, marginBottom: 3 }}>{titulek}</div>
            {text && <div style={{ fontSize: 12, lineHeight: 1.5 }}>{text}</div>}
        </div>
    );

    // Obal: okno (sheet), nebo stránka se stejnou hlavičkou, úvodem a patičkou
    const Obal = jakoStranka ? StrankaObal : SheetModal;

    // ── Detail článku v sheetu ──
    if (detail || nacitamDetail) {
        return (
            <Obal onClose={onClose} isDark={isDark} ikona={BookMarked} barva={akcent} maxSirka={640}
                nadpis={detail?.nazev || 'Načítám…'}
                podnadpis={detail ? [TYPY[detail.typ].label, detail.kod, detail.kategorie].filter(Boolean).join(' · ') : null}
                uvod={(
                    <button type="button" onClick={() => { setDetail(null); setChyba(null); }}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 2, height: 32, padding: '0 8px 0 2px', borderRadius: 8, background: 'none', border: 'none', color: akcent, fontWeight: 600, fontSize: 13.5, cursor: 'pointer', fontFamily: 'inherit' }}>
                        <ChevronLeft size={17} /> {qr ? qr.zarizeni.nazev : 'Všechny Znalosti'}
                    </button>
                )}
                paticka={detail?.smi_spravovat && onUpravit ? (
                    <SheetTlacitko isDark={isDark} onClick={() => onUpravit(detail.id)}>Upravit v modulu Znalosti</SheetTlacitko>
                ) : null}>
                {detail
                    ? <ClanekDetail key={detail.id} clanek={{ ...detail, smi_spravovat: false }} isDark={isDark} isMobile={isMobile}
                        onOtevritClanek={otevrit} vSheetu />
                    : <p style={{ fontSize: 13.5, color: barvy.textMuted, padding: '12px 2px' }}>Načítám…</p>}
            </Obal>
        );
    }

    // ── Seznam ──
    const pocet = clanky.length;
    const podnadpis = dokumentace ? 'Recepty, směrnice a návody'
        : data === null ? 'Načítám…'
        : kObrazovce.nazev ? `${pocet} ${sklonujClanky(pocet)} · obrazovka ${kObrazovce.nazev}`
        : `${pocet} ${sklonujClanky(pocet)} · návody a jednobodovky`;
    const kPotvrzeni = dokumentace?.prehled?.k_potvrzeni || [];
    const nalezenoDok = (vysledkyDok?.smernice?.length || 0) + (vysledkyDok?.recepty?.length || 0);

    return (
        <Obal onClose={onClose} isDark={isDark} ikona={BookMarked} barva={akcent} nadpis="Znalosti" podnadpis={podnadpis}
            uvod={(
                <label style={{
                    display: 'flex', alignItems: 'center', gap: 10, height: SHEET_TOKENS.sizes.inputHeight, padding: '0 12px',
                    borderRadius: SHEET_TOKENS.radius.input, ...vnitrni, color: barvy.textMuted,
                }}>
                    <Search size={17} style={{ flexShrink: 0 }} />
                    <input value={hledani} onChange={e => setHledani(e.target.value)} placeholder={dokumentace ? (maRecepty ? 'Hledat recept, směrnici nebo návod…' : 'Hledat směrnici nebo návod…') : 'Hledat návod nebo jednobodovku…'}
                        style={{ flex: 1, minWidth: 0, border: 'none', outline: 'none', background: 'transparent', fontSize: 15, color: barvy.textPrimary, fontFamily: 'inherit' }} />
                    {hledani && (
                        <button type="button" onClick={() => setHledani('')} aria-label="Smazat hledání"
                            style={{ background: 'none', border: 'none', padding: 4, lineHeight: 0, color: barvy.textMuted, cursor: 'pointer' }}>
                            <X size={16} />
                        </button>
                    )}
                </label>
            )}>
            {chyba && <SheetAlert kind="error" isDark={isDark}>{chyba}</SheetAlert>}

            {nalezene ? (
                nalezene.length === 0 && nalezenoDok === 0
                    ? prazdno('Nic jsme nenašli', 'Zkus jiné slovo, třeba „směna" nebo „fritéza".')
                    : <>
                        {nalezene.length > 0 && skupina('nalezeno', dokumentace ? `Návody · ${nalezene.length}` : `Nalezeno · ${nalezene.length}`, nalezene)}
                        {vysledkyDok?.smernice?.length > 0 && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 10 }}>
                                <SheetNadpisSkupiny isDark={isDark} style={{ margin: '2px 2px 0' }}>Směrnice · {vysledkyDok.smernice.length}</SheetNadpisSkupiny>
                                {vysledkyDok.smernice.map(x => radekDok('smernice', x, [x.kategorie?.nazev, x.cislo_dokumentu].filter(Boolean).join(' · ')))}
                            </div>
                        )}
                        {vysledkyDok?.recepty?.length > 0 && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 10 }}>
                                <SheetNadpisSkupiny isDark={isDark} style={{ margin: '2px 2px 0' }}>Recepty · {vysledkyDok.recepty.length}</SheetNadpisSkupiny>
                                {vysledkyDok.recepty.map(r => radekDok('recepty', r, r.nalezeno_v ? `nalezeno v: ${r.nalezeno_v}` : r.kategorie?.nazev))}
                            </div>
                        )}
                    </>
            ) : (
                <>
                    {kPotvrzeni.length > 0 && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 10 }}>
                            <SheetNadpisSkupiny isDark={isDark} style={{ margin: '2px 2px 0', color: '#FF9F33' }}>K potvrzení · {kPotvrzeni.length}</SheetNadpisSkupiny>
                            {kPotvrzeni.map(x => radekDok(x.druh === 'smernice' ? 'smernice' : 'recepty', x, x.popisek, true))}
                        </div>
                    )}
                    {qr && (qr.clanky.length
                        ? skupina('qr', <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><Wrench size={11} /> {qr.zarizeni.nazev}</span>, qr.clanky, true)
                        : prazdno(qr.zarizeni.nazev, 'K tomuto zařízení zatím nic není.'))}
                    {kObrazovce.clanky?.length > 0 && skupina('tady', kObrazovce.nazev ? `Tady se hodí · ${kObrazovce.nazev}` : 'Tady se hodí', kObrazovce.clanky, true)}
                    {dokumentace && dlazdice()}
                    {dokumentace && <div ref={navodyRef} style={{ scrollMarginTop: 12 }}><SheetNadpisSkupiny isDark={isDark} style={{ margin: '6px 2px 8px', fontSize: 13 }}>Návody a jednobodovky</SheetNadpisSkupiny></div>}
                    {data === null && <p style={{ fontSize: 13, color: barvy.textMuted, padding: '8px 2px' }}>Načítám…</p>}
                    {data && pocet === 0 && prazdno('Zatím tu nic není', 'Návody a jednobodovky sem přidává vedení.')}
                    {podleKategorii.map(s => skupina(s.id, s.nazev, s.clanky))}
                </>
            )}
        </Obal>
    );
}

/**
 * Znalosti jako stránka — hlavička (ikona, nadpis, podnadpis), úvod (hledání
 * nebo „zpět") pevně nahoře, obsah se roluje, dole rezerva pod lištu.
 * Stejné rozhraní jako `SheetModal`, ať se obal dá vyměnit.
 */
function StrankaObal({ isDark, ikona: Ikona, barva, nadpis, podnadpis, uvod, paticka, maxSirka = 720, children }) {
    const barvy = getTextColors(isDark);
    return (
        <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, height: '100%' }}>
            <div style={{ flexShrink: 0, padding: '12px 16px 10px', maxWidth: maxSirka, width: '100%', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    {Ikona && (
                        <span style={{ width: 40, height: 40, borderRadius: 12, flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: `${barva}24`, color: barva }}>
                            <Ikona size={20} />
                        </span>
                    )}
                    <div style={{ minWidth: 0 }}>
                        <h1 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: barvy.textPrimary, lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{nadpis}</h1>
                        {podnadpis && <div style={{ fontSize: 12.5, color: barvy.textMuted, marginTop: 2 }}>{podnadpis}</div>}
                    </div>
                </div>
                {uvod}
            </div>
            <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
                <div style={{ maxWidth: maxSirka, margin: '0 auto', padding: '4px 16px 16px' }}>
                    {children}
                    {paticka && <div style={{ marginTop: 12 }}>{paticka}</div>}
                    <div aria-hidden="true" style={{ height: REZERVA_LISTY }} />
                </div>
            </div>
        </div>
    );
}
